/** @import { Vibe, SectionType, SectionTypeId } from './types.ts' */
// ── CONSTANTS ────────────────────────────────────────────────────
const MODEL = 'claude-sonnet-4-6';
/** @type {Record<SectionTypeId, string>} */
const TC = {
  header: '#1e293b',
  hero: '#7c3aed',
  problem: '#ef4444',
  solution: '#10b981',
  features: '#3b82f6',
  process: '#f59e0b',
  'social-proof': '#06b6d4',
  stats: '#8b5cf6',
  testimonials: '#06b6d4',
  pricing: '#ec4899',
  faq: '#64748b',
  cta: '#ef4444',
  footer: '#374151',
  'trust-ticker': '#0a9e87',
  services: '#3b82f6',
  about: '#8b5cf6',
  'service-area': '#f59e0b',
  contact: '#10b981',
  subscribe: '#06b6d4',
  video: '#dc2626',
  content: '#475569',
};
const DW = { desktop: '100%', tablet: '768px', mobile: '390px' };
const DI = {
  desktop:
    '<svg width="16" height="14" viewBox="0 0 16 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="1" width="14" height="9" rx="1.5"/><line x1="5" y1="13" x2="11" y2="13"/><line x1="8" y1="10" x2="8" y2="13"/></svg>',
  tablet:
    '<svg width="12" height="16" viewBox="0 0 12 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="1" width="10" height="14" rx="1.5"/><circle cx="6" cy="13" r=".8" fill="currentColor" stroke="none"/></svg>',
  mobile:
    '<svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="1" width="8" height="14" rx="1.5"/><circle cx="5" cy="13" r=".8" fill="currentColor" stroke="none"/></svg>',
};
/** @type {Vibe[]} */
const VIBES = [
  {
    id: 'clean',
    name: 'Clean & trusted',
    sub: 'health, finance, B2B',
    colors: ['#f8fafc', '#e2e8f0', '#3b82f6', '#1d4ed8'],
  },
  {
    id: 'bold',
    name: 'Bold & challenger',
    sub: 'DTC, consumer, disruptive',
    colors: ['#09090b', '#18181b', '#f59e0b', '#fbbf24'],
  },
  {
    id: 'luxury',
    name: 'Luxury & premium',
    sub: 'high-end, exclusive, refined',
    colors: ['#0c0a09', '#1c1917', '#d4a853', '#a07830'],
  },
  {
    id: 'warm',
    name: 'Warm & human',
    sub: 'coaching, wellness, community',
    colors: ['#fffbeb', '#fef3c7', '#d97706', '#92400e'],
  },
  {
    id: 'technical',
    name: 'Technical & precise',
    sub: 'dev tools, SaaS, engineering',
    colors: ['#030712', '#111827', '#6366f1', '#818cf8'],
  },
  {
    id: 'playful',
    name: 'Playful & friendly',
    sub: 'education, consumer, fun',
    colors: ['#fdf4ff', '#fae8ff', '#a855f7', '#7e22ce'],
  },
];
const EDIT_EX = [
  'Make the hero headline more urgent',
  'Add a money-back guarantee badge near the CTA',
  'Move testimonials above the FAQ section',
  'Make the stats numbers larger and more impactful',
  'Tighten the CTA — shorter copy, bigger button',
  'Add urgency language throughout (limited availability)',
];
const BQ = [
  {
    tag: 'About your business',
    unlocks: 'industry detection, delivery model, maturity signals',
    why: 'The way you naturally describe your business gives AI the tone and language to generate copy that sounds like you.',
    ex: null,
  },
  {
    tag: 'Your customer',
    unlocks: 'buyer persona, pain point, emotional state, implied objections',
    why: 'Understanding who finds you and why determines every headline, benefit statement, and objection-handling FAQ.',
    ex: null,
  },
  {
    tag: 'Conversion goal',
    unlocks: 'CTA buttons, lead magnet, form placement, urgency',
    why: 'The desired action shapes the entire page structure. High-price purchases need more trust signals, self-serve signups need less friction.',
    ex: null,
  },
  {
    tag: 'Visual direction',
    unlocks: 'color palette, typography, design vision, section style',
    why: 'Gives the AI a real palette direction. Logo upload extracts your actual brand colors.',
    ex: null,
  },
];
const ACTION_OPTS = [
  { id: 'call', label: 'Book a call or consultation' },
  { id: 'buy', label: 'Buy a product' },
  { id: 'signup', label: 'Sign up or start a free trial' },
  { id: 'quote', label: 'Request a quote' },
  { id: 'contact', label: 'Get in touch' },
  { id: 'other', label: 'Something else' },
];
const PRICE_OPTS = ['Under $50', '$50 - $500', '$500 - $5,000', '$5,000+'];
const GAP_SYS = `<role>
You are a conversion strategist identifying gaps in a website brief.
</role>

<task>
Analyze the brief and identify 2-3 critical information gaps that would improve the website's conversion.
</task>

<rules>
- Exactly 2-3 gaps. One concrete question each.
- Categories: DIFFERENTIATION, PROOF, OBJECTION, TRANSFORMATION, PROCESS, LOCAL.
- Questions must be specific and actionable — not generic.
- Never re-ask information already in the brief.
- Never ask about visual preferences or design.
</rules>`;

// NOTE: All \u003cscript\u003e and \u003c/script\u003e tags inside prompt strings use unicode escapes
// to prevent the browser HTML parser from treating them as real script tags.
// \u003c = <    \u003e = >    so \u003cscript\u003e = <script>

/** Default max_tokens for all section types */
const SECTION_TOKENS_DEFAULT = 10000;

/** @type {SectionType[]} */
const SECT_TYPES = [
  { id: 'header', color: '#1e293b', desc: 'Sticky nav with logo, links, CTA' },
  { id: 'hero', color: '#7c3aed', desc: 'Headline, subhead, CTA, visual' },
  { id: 'trust-ticker', color: '#0a9e87', desc: 'Horizontal bar with trust promises' },
  { id: 'problem', color: '#ef4444', desc: 'Pain points, cost of inaction' },
  { id: 'solution', color: '#10b981', desc: 'Product as the answer' },
  { id: 'features', color: '#3b82f6', desc: 'Grid of capabilities & benefits' },
  { id: 'services', color: '#3b82f6', desc: 'Horizontal cards with image + description' },
  { id: 'process', color: '#f59e0b', desc: 'Step-by-step how it works' },
  { id: 'about', color: '#8b5cf6', desc: 'Owner spotlight with photo & credentials' },
  { id: 'social-proof', color: '#06b6d4', desc: 'Logo strip, trust indicators' },
  { id: 'stats', color: '#8b5cf6', desc: 'Oversized numbers, results' },
  { id: 'testimonials', color: '#06b6d4', desc: 'Quote cards with attribution' },
  { id: 'service-area', color: '#f59e0b', desc: 'Location pills showing areas served' },
  { id: 'pricing', color: '#ec4899', desc: 'Tier cards, recommended plan' },
  { id: 'faq', color: '#64748b', desc: 'Objection-handling Q&As' },
  { id: 'contact', color: '#10b981', desc: 'Contact form + business info' },
  { id: 'subscribe', color: '#06b6d4', desc: 'Email subscribe form with notification' },
  { id: 'video', color: '#dc2626', desc: 'Video embed with facade play button' },
  { id: 'content', color: '#475569', desc: 'Custom section — describe any content' },
  { id: 'cta', color: '#ef4444', desc: 'Final conversion push' },
  { id: 'footer', color: '#374151', desc: 'Links, legal, copyright' },
];

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  ACTION_OPTS,
  BQ,
  DI,
  DW,
  EDIT_EX,
  GAP_SYS,
  MODEL,
  PRICE_OPTS,
  SECT_TYPES,
  SECTION_TOKENS_DEFAULT,
  TC,
  VIBES,
};
